// components/scans/ScanCategoriesTable.jsx
import React from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Tooltip,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Stack
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Restore as RestoreIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

const ScanCategoriesTable = ({
    categories,
    loading,
    canEdit,
    canDelete,
    onEdit,
    onDelete,
    onRestore,
    page,
    rowsPerPage,
    total,
    onPageChange,
    onRowsPerPageChange,
    filters
}) => {
    const { t } = useTranslation();

    const formatDate = (date) => {
        return format(new Date(date), 'dd/MM/yyyy');
    };

    const getEmptyMessage = () => {
        if (filters.search) {
            return t('scanCategories.noSearchResults', 'No categories match your search');
        }
        if (filters.status === 'active') {
            return t('scanCategories.noActiveCategories', 'No active categories found');
        }
        if (filters.status === 'inactive') {
            return t('scanCategories.noInactiveCategories', 'No inactive categories found');
        }
        return t('scanCategories.noCategoriesDescription', 'No categories found');
    };

    return (
        <>
            <Paper sx={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('scanCategories.name', 'Name')}</TableCell>
                                <TableCell>{t('scanCategories.description', 'Description')}</TableCell>
                                <TableCell>{t('scanCategories.color', 'Color')}</TableCell>
                                <TableCell align="center">{t('common.status', 'Status')}</TableCell>
                                <TableCell>{t('scanCategories.created', 'Created')}</TableCell>
                                <TableCell>{t('scanCategories.actions', 'Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : categories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            {getEmptyMessage()}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                categories.map((category) => (
                                    <TableRow
                                        key={category._id}
                                        hover
                                        sx={{
                                            opacity: !category.isActive ? 0.6 : 1
                                        }}
                                    >
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Box
                                                    sx={{
                                                        width: 12,
                                                        height: 12,
                                                        borderRadius: '50%',
                                                        backgroundColor: category.color,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        flexShrink: 0
                                                    }}
                                                />
                                                <Typography variant="body2" fontWeight="medium">
                                                    {category.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    maxWidth: 300,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {category.description || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Box
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: 1,
                                                        backgroundColor: category.color,
                                                        border: '1px solid',
                                                        borderColor: 'divider'
                                                    }}
                                                />
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {category.color}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={category.isActive ? t('common.active') : t('common.inactive')}
                                                color={category.isActive ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(category.createdAt)}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                {canEdit && (
                                                    <Tooltip title={t('common.edit', 'Edit')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onEdit(category)}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && category.isActive && (
                                                    <Tooltip title={t('common.delete', 'Delete')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onDelete(category)}
                                                        >
                                                            <DeleteIcon color="error" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {!category.isActive && canEdit && (
                                                    <Tooltip title={t('common.restore', 'Restore')}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onRestore(category._id)}
                                                            color="primary"
                                                        >
                                                            <RestoreIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>


            </Paper>
            <Paper sx={{ width: '100%', mt: 2 }}>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={onPageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={onRowsPerPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
                />
            </Paper>
        </>
    );
};

export default ScanCategoriesTable;